import { api } from "./api";

/**
 * Resize+recompress a browser File so the upload payload is small and fast,
 * regardless of how big the original photo is (modern phones easily produce
 * 30–60 MB HEIC/JPG). This eliminates the "Network Error" failure mode where
 * large multipart uploads get dropped by intermediate proxies/flaky networks.
 *
 * Strategy:
 *   1. Skip preprocessing for HEIC/HEIF (the browser canvas can't decode it)
 *      and for files already <= 1.5 MB — let the server handle them.
 *   2. Decode the image via `createImageBitmap` with EXIF orientation honoured,
 *      draw onto a canvas resized so the longest edge is <= 2400 px,
 *      re-encode as JPEG quality 0.88.
 *   3. Fall back to the raw File if anything in the pipeline throws.
 *
 * Result: any phone photo → ~300–900 KB JPEG → uploads in <1 s on any network.
 */
const MAX_EDGE = 2400;
const JPEG_QUALITY = 0.88;
const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024;

async function preprocessImage(file) {
  const isHeic =
    /heic|heif/i.test(file.type || "") ||
    /\.(heic|heif)$/i.test(file.name || "");
  if (isHeic) return file; // server-side pillow-heif handles HEIC
  if (file.size <= SKIP_BELOW_BYTES) return file;

  try {
    // `imageOrientation: 'from-image'` applies EXIF rotation natively — keeps
    // portrait phone shots upright without manual matrix math.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const longest = Math.max(bitmap.width, bitmap.height);
    const ratio = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
    const w = Math.round(bitmap.width * ratio);
    const h = Math.round(bitmap.height * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY)
    );
    if (!blob) return file;

    // Wrap blob back into a File with a .jpg extension so the server's
    // extension check passes for the recompressed payload.
    const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (err) {
    // Anything failed — safer to upload the raw bytes than block the user.
    // The server-side Pillow pipeline will recompress on the backend.
    // eslint-disable-next-line no-console
    console.warn("[uploadImage] preprocess failed, uploading raw:", err);
    return file;
  }
}

/**
 * Upload an image via POST /api/admin/upload. Compresses client-side first,
 * retries once on transient network errors, and surfaces a useful error
 * message instead of axios's generic "Network Error".
 */
export async function uploadImage(file) {
  if (!file) throw new Error("No file selected");
  if (!/^image\//i.test(file.type) && !/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name || "")) {
    throw new Error("Only JPG, PNG, WEBP, GIF or HEIC images are supported.");
  }

  const prepared = await preprocessImage(file);

  const send = async () => {
    const fd = new FormData();
    fd.append("file", prepared, prepared.name || file.name || "upload.jpg");
    // 5 min timeout to cover slow mobile networks
    const r = await api.post("/admin/upload", fd, { timeout: 300_000 });
    return r.data.url;
  };

  try {
    return await send();
  } catch (err) {
    // If it's a clear server error (4xx/5xx with body), don't retry — surface it.
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status && detail) {
      throw new Error(typeof detail === "string" ? detail : "Upload rejected by server");
    }
    // Otherwise it's a transient network blip — retry once.
    try {
      // eslint-disable-next-line no-console
      console.warn("[uploadImage] first attempt failed, retrying once:", err?.message || err);
      await new Promise((r) => setTimeout(r, 800));
      return await send();
    } catch (err2) {
      const s2 = err2?.response?.status;
      const d2 = err2?.response?.data?.detail;
      if (s2 && d2) {
        throw new Error(typeof d2 === "string" ? d2 : "Upload rejected by server");
      }
      throw new Error(
        "Couldn't upload the image. Please check your internet connection and try again, or pick a smaller photo."
      );
    }
  }
}
